/* eslint-disable operator-assignment,no-nested-ternary */
import { useEffect, useMemo, useState, useTransition } from 'react'
import { ButtonGroup } from '@mui/material'
import { useStyles } from 'platform/react/hook'
import { debounce, getThemeColor, isNum, PlatformEvent } from 'lib/util'
import SimpleButton from 'ui/Element/Button'

const styles = (theme, { color }) => ({
  root: {
    maxWidth: '100%',
    borderRadius: '0.5rem',
    border: `0.0625rem solid ${theme.palette.divider}`,
    '& > button': {
      '&:nth-child(2)': { // center button
        '&:disabled': {
          border: 'none',
          color: getThemeColor(theme, color),
        },
      },
      border: 'none',
      ...color && {
        color: getThemeColor(theme, color),
      },
      '&:disabled': {
        border: 'none',
        color: theme.palette.divider,
      },
      '&:hover': {
        border: 'none',
        ...color && {
          color: getThemeColor(theme, color),
        },
      },
    },
  },
  button: {
    width: 'calc(1/3)!important',
    borderRadius: '0!important',
    borderColor: 'transparent!important',
  },
})

/**
 * Amount Component
 *
 * Displays a button group consisting for a +, -, and value button giving the user a menu to
 * increase or decrease a given value.
 *
 * The `onClick` handlers of the + and - buttons are debounced. Their handlers will record
 * every click of the button, and when the debounce delay expired, the business event handler will
 * be called with the current value + times the button was clicked. Once the debounce delay expired,
 * the buttons will be disabled for the duration of the operation so that another click cannot
 * interfere until we have a result.
 *
 * @param {number} [value=0]      the value to display
 * @param {number} [min=1]        the minimum value
 * @param {number|undefined} max  the maximum value
 * @param {Object} events         events for the component
 * @param {Object} props          additional props
 * @returns {null|JSX.Element}
 */
const Amount = ({ value: initialValue = 0, min = 1, max = undefined, events, ...props }) => {
  const {
    color,
    size,
    variant = 'outlined',
    disabledWhenOffline,
    disabledWhilePersisting = true,
    debounced = 200,
  } = props
  const { onChange = null } = events

  const classes = useStyles(styles, { color })()
  const [, startTransition] = useTransition()
  const [disabled, setDisabled] = useState(false)
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    setValue(initialValue)
    disabled && disabledWhilePersisting && setDisabled(false)
  }, [initialValue])

  useEffect(() => {
    disabledWhilePersisting
      && startTransition(() => { setDisabled(true) })

    if (value && value !== initialValue) {
      const currentValue = value < min
        ? min
        : value > max
          ? max
          : value

      onChange?.(new PlatformEvent('change', { value: currentValue }))
    }
  }, [value, initialValue])

  const handleIncrease = useMemo(() => {
    const fn = () => setValue(v => (!max || v + 1 <= max ? v + 1 : v))
    return isNum(debounced) && debounced > 0 ? debounce(fn, debounced) : fn
  }, [max, debounced])

  const handleDecrease = useMemo(() => {
    const fn = () => setValue(v => (v - 1 >= min ? v - 1 : v))
    return isNum(debounced) && debounced > 0 ? debounce(fn, debounced) : fn
  }, [min, debounced])

  const showIncrease = useMemo(() => (isNum(max) ? value < max : true), [max, value])
  const showDecrease = useMemo(() => (isNum(min) ? value > min : true), [min, value])

  return props?.hidden ? null : (
    <ButtonGroup
      disableFocusRipple
      size={size}
      variant={variant}
      className={classes.root}
    >
      <SimpleButton
        icon={{
          size: '1.25rem',
          name: 'minus',
          color: disabled || !showDecrease
            ? 'gray.900'
            : 'black.main',
        }}
        disabled={disabled || !showDecrease}
        disabledWhenOffline={disabledWhenOffline}
        onClick={handleDecrease}
        className={classes.button}
      />
      <SimpleButton
        disabled
        value={value}
        className={classes.button}
      />
      <SimpleButton
        icon={{
          size: '1.25rem',
          name: 'plus',
          color: disabled || !showIncrease
            ? 'gray.900'
            : 'black.main',
        }}
        disabled={disabled || !showIncrease}
        disabledWhenOffline={disabledWhenOffline}
        onClick={handleIncrease}
        className={classes.button}
      />
    </ButtonGroup>
  )
}

export default Amount
